import { PLAN_CODES } from '@athena/shared'
import { StatTile, useTranslate } from '@athena/ui'

import { useFormat } from '../../lib/format.js'
import type { SubscriptionFieldsFragment } from '../../graphql/generated/operations.js'

export interface UsageSummaryProps {
  subscription: SubscriptionFieldsFragment
}

const USAGE_METRIC_CLASS =
  'rounded-none border-0 bg-transparent p-0 py-4 first:pt-0 last:pb-0 sm:px-4 sm:py-0 sm:first:pl-0 sm:last:pr-0'

/**
 * What the tutor is using, and what the plan allows — two different kinds of
 * number, drawn two different ways.
 *
 * ── Usage ────────────────────────────────────────────────────────────────
 *
 * Students, active practices and AI generations are consumed, so each reads
 * "used of limit" in one divided metric group. Every figure is the server's:
 * nothing here divides, subtracts or decides what "remaining" means — the
 * entitlement service computed all of it from one snapshot, so the metrics
 * are one moment.
 * A metric at its limit says so in a sentence, and says what can be done when
 * there is a larger plan to ask for. Said in the ordinary text colour: a full
 * plan is a fact about the account, not an error.
 *
 * The AI tile is the only one with a reset date, and on Free it deliberately
 * has none: three introductory generations do not come back.
 *
 * ── Plan limits ──────────────────────────────────────────────────────────
 *
 * Questions per practice is not consumed — every practice can have that many
 * — so it is not a tile in the "x of y" shape but a line of what the plan
 * lets you do, beside whether it includes timed practices. The limit is the
 * server's; timed practices are read from the plan catalog, the one place a
 * plan's features are written down.
 */
export function UsageSummary({ subscription }: UsageSummaryProps) {
  const t = useTranslate()
  const format = useFormat()

  const { students, practices, ai, planCode, features } = subscription
  const largerPlanExists = PLAN_CODES.indexOf(planCode) < PLAN_CODES.length - 1
  const timedIncluded = features.timedPractices

  /** The sentence a full tile carries, or null when it is not full. */
  function atLimit(
    remaining: number | null,
    full: 'subscription.atLimitStudents' | 'subscription.atLimitPractices',
  ) {
    if (remaining === null || remaining > 0) return null
    return largerPlanExists ? `${t(full)} ${t('subscription.atLimitAskLarger')}` : t(full)
  }

  const studentsFull = atLimit(students.remaining, 'subscription.atLimitStudents')
  const practicesFull = atLimit(practices.remaining, 'subscription.atLimitPractices')

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <StatTile
          className={USAGE_METRIC_CLASS}
          label={t('subscription.studentsLabel')}
          value={t('subscription.ofLimit', [students.used, students.limit])}
          detail={
            students.reserved === 1
              ? t('subscription.invitationsReservedOne')
              : students.reserved > 1
                ? t('subscription.invitationsReservedOther', [students.reserved])
                : t('subscription.remainingCount', [students.remaining])
          }
        >
          {studentsFull ? <LimitNote>{studentsFull}</LimitNote> : null}
        </StatTile>
        <StatTile
          className={USAGE_METRIC_CLASS}
          label={t('subscription.practicesLabel')}
          value={
            practices.limit === null
              ? t('subscription.unlimitedUsage', [practices.used])
              : t('subscription.ofLimit', [practices.used, practices.limit])
          }
          detail={
            practices.remaining === null
              ? t('subscription.unlimitedCapacity')
              : t('subscription.remainingCount', [practices.remaining])
          }
        >
          {practicesFull ? <LimitNote>{practicesFull}</LimitNote> : null}
        </StatTile>
        <StatTile
          className={USAGE_METRIC_CLASS}
          label={t('subscription.aiLabel')}
          value={t('subscription.ofLimit', [ai.consumed, ai.allowance])}
          detail={aiDetail()}
        />
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <h3 className="text-h4 font-semibold text-text">{t('subscription.planLimitsHeading')}</h3>
        <ul className="flex flex-col gap-1 text-body text-text">
          <li>{t('subscription.questionsUpTo', [subscription.questionLimit])}</li>
          <li>
            {t(timedIncluded ? 'subscription.timedIncluded' : 'subscription.timedNotIncluded')}
          </li>
        </ul>
      </div>
    </div>
  )

  function aiDetail(): string {
    // In-flight generations first: a tutor who has just pressed Generate and
    // is reading this wants to know the credit is accounted for rather than
    // missing.
    if (ai.reserved > 0) return t('subscription.reservedNote', [ai.reserved])
    if (ai.remaining === 0) return t('subscription.aiNoneLeft')
    if (ai.kind === 'LIFETIME') return t('subscription.aiIntroductory')
    return ai.resetsAt
      ? t('subscription.aiResets', [format.date(ai.resetsAt)])
      : t('subscription.remainingCount', [ai.remaining])
  }
}

/** A metric's at-limit sentence: separated by a rule and set in body text, never red. */
function LimitNote({ children }: { children: string }) {
  return (
    <p className="mt-2 border-t border-border pt-2 text-body-sm font-medium text-text">
      {children}
    </p>
  )
}
